import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import {
  Button,
  Typography,
  Spin,
  Empty,
  Tag,
  Tooltip,
  Popconfirm,
  message,
  Input,
  Radio,
  Tabs,
  Modal,
  Select,
  Pagination,
  Dropdown,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  SendOutlined,
  SearchOutlined,
  StopOutlined,
  LinkOutlined,
  ArrowLeftOutlined,
  DeleteOutlined,
  UndoOutlined,
  MailOutlined,
  CopyOutlined,
  ExperimentOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import GestionPlaylistDrawer from '../components/Gestion/GestionPlaylistDrawer';
import MessageEditorDrawer from '../components/Gestion/MessageEditorDrawer';
import GestionBroadcastDrawer from '../components/Gestion/GestionBroadcastDrawer';
import GestionBroadcastDetailDrawer from '../components/Gestion/GestionBroadcastDetailDrawer';

const { Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL;

const CLIENT_LABELS = {
  all: 'Tous',
  only_client: 'Clients uniquement',
  only_super: 'Super clients uniquement',
  exclude_client: 'Exclure clients',
  exclude_super: 'Exclure super clients',
};

// Mapping unifié des statuts broadcasts (utilisé sidebar + activité)
const broadcastMatchesStatus = (b, key) => {
  if (key === 'preparing') return b.status === 'new' && !b.scheduled_start_at;
  if (key === 'scheduled') return b.status === 'new' && !!b.scheduled_start_at;
  if (key === 'running') return b.status === 'active' || b.status === 'paused';
  if (key === 'completed') return b.status === 'completed';
  return true;
};

// Palette pastel partagée (suivi campagnes + sidebar)
const STATUS_PALETTE = {
  preparing: { bg: '#f3e8ff', fg: '#7c3aed', border: '#e9d5ff' },
  scheduled: { bg: '#dbeafe', fg: '#2563eb', border: '#bfdbfe' },
  running: { bg: '#dcfce7', fg: '#16a34a', border: '#bbf7d0' },
  paused: { bg: '#ffedd5', fg: '#ea580c', border: '#fed7aa' },
  completed: { bg: '#e2e8f0', fg: '#475569', border: '#cbd5e1' },
};

// Date de référence par broadcast (utilisée pour tri chrono + bloc date)
const broadcastRefDate = b => {
  if (!b) return null;
  if (b.status === 'new') return b.scheduled_start_at || b.created_at;
  if (b.status === 'active' || b.status === 'paused')
    return b.activated_at || b.updated_at;
  if (b.status === 'completed') return b.updated_at || b.activated_at;
  return b.created_at;
};

const formatBroadcastDate = b => {
  const d = broadcastRefDate(b);
  if (!d) return { day: '—', month: '', label: '' };
  const date = new Date(d);
  const day = date.toLocaleDateString('fr-FR', { day: '2-digit' });
  const month = date.toLocaleDateString('fr-FR', { month: 'short' });
  const year = date.getFullYear();
  const labelByStatus = {
    new: b.scheduled_start_at ? 'Lancement' : 'Créé le',
    active: 'Démarré le',
    paused: 'Démarré le',
    completed: 'Terminé le',
  };
  return {
    day,
    month: `${month} ${year}`,
    label: labelByStatus[b.status] || '',
  };
};

const renderBroadcastStatusTag = b => {
  if (b.status === 'new' && b.scheduled_start_at)
    return pastelTag(STATUS_PALETTE.scheduled, 'Programmé');
  if (b.status === 'new')
    return pastelTag(STATUS_PALETTE.preparing, 'En préparation');
  if (b.status === 'active')
    return pastelTag(STATUS_PALETTE.running, 'En cours');
  if (b.status === 'paused')
    return pastelTag(STATUS_PALETTE.paused, 'En pause');
  if (b.status === 'completed')
    return pastelTag(STATUS_PALETTE.completed, 'Terminé');
  return null;
};

const pastelTag = (palette, label, extraStyle = {}) => (
  <Tag
    style={{
      background: palette.bg,
      color: palette.fg,
      border: `1px solid ${palette.border}`,
      borderRadius: 999,
      fontWeight: 600,
      fontSize: 11,
      padding: '2px 10px',
      margin: 0,
      ...extraStyle,
    }}
  >
    {label}
  </Tag>
);

export default function GestionPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tags, setTags] = useState([]);
  const [activites, setActivites] = useState([]);
  const [regions, setRegions] = useState([]);
  const [departements, setDepartements] = useState([]);

  // Right panel state
  const [rightPanel, setRightPanel] = useState(null); // null | 'contacts'
  const [selectedPlaylistId, setSelectedPlaylistId] = useState(null);
  const [activityFilter, setActivityFilter] = useState('all'); // all | scheduled | running | completed
  const [activitySearch, setActivitySearch] = useState('');
  const [activitySortDesc, setActivitySortDesc] = useState(true);

  // Search for left panel
  const [playlistSearch, setPlaylistSearch] = useState('');
  const [playlistSortAsc, setPlaylistSortAsc] = useState(false);
  const [playlistStatusFilter, setPlaylistStatusFilter] = useState(null); // null | 'preparing' | 'scheduled' | 'running' | 'completed'
  const [messageSearch, setMessageSearch] = useState('');
  const [playlistCounts, setPlaylistCounts] = useState({});

  // Messages
  const [messages, setMessages] = useState([]);
  const [messageDrawerOpen, setMessageDrawerOpen] = useState(false);
  const [editMessage, setEditMessage] = useState(null);

  // Broadcast
  const [broadcastDrawerOpen, setBroadcastDrawerOpen] = useState(false);
  const [broadcastPlaylist, setBroadcastPlaylist] = useState(null);
  const [duplicateFrom, setDuplicateFrom] = useState(null);
  const [detailBroadcast, setDetailBroadcast] = useState(null);
  const [broadcastSearch, setBroadcastSearch] = useState('');
  const [broadcastPage, setBroadcastPage] = useState(1);
  const broadcastPageSize = 4;
  const [playlistBroadcasts, setPlaylistBroadcasts] = useState({});
  const [archivedBroadcasts, setArchivedBroadcasts] = useState([]);

  // Test message
  const [testMessageId, setTestMessageId] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testProviderId, setTestProviderId] = useState(333);
  const [testProviders, setTestProviders] = useState([]);
  const [testSending, setTestSending] = useState(false);

  // Drawer for create/edit playlist
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editPlaylist, setEditPlaylist] = useState(null);

  // Contacts panel state
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [excludedCount, setExcludedCount] = useState(0);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsSearch, setContactsSearch] = useState('');
  const [contactsFilter, setContactsFilter] = useState('active'); // 'active' | 'excluded' | 'lr'
  const [contactsLoading, setContactsLoading] = useState(false);
  const [excludeLoading, setExcludeLoading] = useState(null);

  const pageSize = 50;
  const token = localStorage.getItem('prae.jwt');
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  const fetchAllBroadcasts = useCallback(async () => {
    if (!user) return;
    try {
      const baseUrl = isAdmin
        ? `${API_URL}/gestion-broadcasts`
        : `${API_URL}/gestion-broadcasts?userId=${user.id}`;
      const [activeRes, archivedRes] = await Promise.all([
        fetch(baseUrl, { headers }),
        fetch(baseUrl + (baseUrl.includes('?') ? '&' : '?') + 'archived=true', {
          headers,
        }),
      ]);
      const [activeJson, archivedJson] = await Promise.all([
        activeRes.json(),
        archivedRes.json(),
      ]);
      const byPlaylist = {};
      (Array.isArray(activeJson) ? activeJson : []).forEach(b => {
        if (!byPlaylist[b.playlist_id]) byPlaylist[b.playlist_id] = [];
        byPlaylist[b.playlist_id].push(b);
      });
      setPlaylistBroadcasts(byPlaylist);
      setArchivedBroadcasts(Array.isArray(archivedJson) ? archivedJson : []);
    } catch {
      setPlaylistBroadcasts({});
      setArchivedBroadcasts([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  const fetchMessages = useCallback(async () => {
    if (!user) return;
    try {
      const url = isAdmin
        ? `${API_URL}/messages/library`
        : `${API_URL}/messages/library?userId=${user.id}`;
      const res = await fetch(url, { headers });
      const json = await res.json();
      setMessages(Array.isArray(json) ? json : []);
    } catch {
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  const fetchPlaylists = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const url = isAdmin
        ? `${API_URL}/playlists`
        : `${API_URL}/playlists?userId=${user.id}`;
      const res = await fetch(url, { headers });
      const json = await res.json();
      setPlaylists(
        (Array.isArray(json) ? json : []).filter(
          p => p.source_type === 'gestion'
        )
      );
    } catch {
      setPlaylists([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/playlists/gestion/tags`, { headers: jsonHeaders }).then(
        r => r.json()
      ),
      fetch(`${API_URL}/playlists/gestion/activites`, {
        headers: jsonHeaders,
      }).then(r => r.json()),
      fetch(`${API_URL}/playlists/gestion/regions`, {
        headers: jsonHeaders,
      }).then(r => r.json()),
      fetch(`${API_URL}/playlists/gestion/departements`, {
        headers: jsonHeaders,
      }).then(r => r.json()),
    ])
      .then(([t, a, r, d]) => {
        setTags(Array.isArray(t) ? t : []);
        setActivites(Array.isArray(a) ? a : []);
        setRegions(Array.isArray(r) ? r : []);
        setDepartements(Array.isArray(d) ? d : []);
      })
      .catch(() => {});
    fetchPlaylists();
    fetchMessages();
    fetchAllBroadcasts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Initialiser les counts depuis last_count (stocké en DB, pas d'appel Laravel)
  useEffect(() => {
    if (!playlists.length) return;
    setPlaylistCounts(prev => {
      const next = { ...prev };
      playlists.forEach(p => {
        if (p.last_count != null && next[p.id] === undefined) {
          next[p.id] = p.last_count;
        }
      });
      return next;
    });
  }, [playlists]);

  // ─── Contacts fetching ─────────────────────────────────
  const fetchContacts = useCallback(
    async (playlistId, page = 1, search = '', filterView = 'active') => {
      if (!playlistId) return;
      setContactsLoading(true);
      try {
        const res = await fetch(
          `${API_URL}/playlists/${playlistId}/gestion-contacts`,
          {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify({
              page,
              page_size: pageSize,
              search,
              filter_view: filterView,
            }),
          }
        );
        const json = await res.json();
        setContacts(json.contacts || []);
        setContactsTotal(json.totalAll || 0);
        setExcludedCount(json.excluded_count || 0);
      } catch {
        setContacts([]);
      } finally {
        setContactsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Polling silencieux pour la vue contacts (toutes les 3s)
  // Ne met à jour que les contacts qui ont changé pour ne pas déranger l'utilisateur
  useEffect(() => {
    if (rightPanel !== 'contacts' || !selectedPlaylistId) return;

    const pollSilently = async () => {
      if (document.hidden) return;
      try {
        const res = await fetch(
          `${API_URL}/playlists/${selectedPlaylistId}/gestion-contacts`,
          {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify({
              page: contactsPage,
              page_size: pageSize,
              search: contactsSearch,
              filter_view: contactsFilter,
            }),
          }
        );
        const json = await res.json();
        const fresh = json.contacts || [];
        const freshTotal = json.totalAll || 0;

        // Mettre à jour la pastille bleue si le total a changé
        setPlaylistCounts(prev => {
          if (prev[selectedPlaylistId] === freshTotal) return prev;
          return { ...prev, [selectedPlaylistId]: freshTotal };
        });

        // Toujours mettre à jour les totaux du header
        setContactsTotal(freshTotal);
        setExcludedCount(json.excluded_count || 0);

        setContacts(prev => {
          // Si nombre de contacts différent → tout remplacer
          if (prev.length !== fresh.length) {
            return fresh;
          }
          // Sinon merge : garder les références identiques pour les contacts inchangés
          let hasChanges = false;
          const merged = fresh.map(newC => {
            const oldC = prev.find(p => p.id === newC.id);
            if (!oldC) {
              hasChanges = true;
              return newC;
            }
            // Comparer les champs susceptibles de changer
            const oldKey = JSON.stringify({
              t: oldC.tags,
              lr: oldC.is_lr,
              ex: oldC.is_excluded,
              em: oldC.email,
              tel: oldC.telephone,
              nom: oldC.nom,
              prenom: oldC.prenom,
              nc: oldC.nom_commercial,
              civ: oldC.civilite,
            });
            const newKey = JSON.stringify({
              t: newC.tags,
              lr: newC.is_lr,
              ex: newC.is_excluded,
              em: newC.email,
              tel: newC.telephone,
              nom: newC.nom,
              prenom: newC.prenom,
              nc: newC.nom_commercial,
              civ: newC.civilite,
            });
            if (oldKey === newKey) return oldC; // Référence préservée
            hasChanges = true;
            return newC;
          });
          if (!hasChanges) return prev; // Aucun changement → pas de re-render
          return merged;
        });
      } catch {
        /* ignore */
      }
    };

    const interval = setInterval(pollSilently, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rightPanel,
    selectedPlaylistId,
    contactsPage,
    contactsSearch,
    contactsFilter,
  ]);

  const openContacts = playlistId => {
    setSelectedPlaylistId(playlistId);
    setRightPanel('contacts');
    setContactsPage(1);
    setContactsSearch('');
    setContactsFilter('active');
    fetchContacts(playlistId, 1, '', 'active');
  };

  const openTestMessage = async msgId => {
    setTestMessageId(msgId);
    setTestEmail('');
    setTestProviderId(333);
    if (!testProviders.length) {
      try {
        const res = await fetch(`${API_URL}/smtp-providers`, { headers });
        const data = await res.json();
        setTestProviders(
          (Array.isArray(data) ? data : []).filter(p => !p.deleted_at)
        );
      } catch {
        /* ignore */
      }
    }
  };

  const handleSendTest = async () => {
    if (!testEmail || !testProviderId || !testMessageId) return;
    setTestSending(true);
    try {
      const res = await fetch(
        `${API_URL}/messages/library/${testMessageId}/test`,
        {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({
            email: testEmail,
            smtp_provider_id: testProviderId,
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      message.success(`Test envoyé à ${testEmail}`);
      setTestMessageId(null);
    } catch (err) {
      message.error(`Erreur : ${err.message}`);
    } finally {
      setTestSending(false);
    }
  };

  const handleDuplicateMessage = async msgId => {
    try {
      const res = await fetch(
        `${API_URL}/messages/library/${msgId}/duplicate`,
        { method: 'POST', headers: jsonHeaders }
      );
      const newMsg = await res.json();
      message.success('Message dupliqué');
      await fetchMessages();
      setEditMessage(newMsg);
      setMessageDrawerOpen(true);
    } catch {
      message.error('Erreur duplication');
    }
  };

  const handleDeleteMessage = async msgId => {
    try {
      await fetch(`${API_URL}/messages/library/${msgId}`, {
        method: 'DELETE',
        headers: jsonHeaders,
      });
      message.success('Message archivé');
      fetchMessages();
    } catch {
      message.error('Erreur archivage');
    }
  };

  const handleArchive = async playlistId => {
    try {
      await fetch(`${API_URL}/playlists/${playlistId}`, {
        method: 'DELETE',
        headers: jsonHeaders,
      });
      message.success('Playlist archivée');
      fetchPlaylists();
      if (selectedPlaylistId === playlistId) {
        setRightPanel(null);
        setSelectedPlaylistId(null);
      }
    } catch {
      message.error('Erreur archivage');
    }
  };

  const refreshPlaylistCount = async playlistId => {
    try {
      const res = await fetch(
        `${API_URL}/playlists/${playlistId}/gestion-contacts`,
        {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({
            page: 1,
            page_size: 1,
            filter_view: 'active',
          }),
        }
      );
      const json = await res.json();
      setPlaylistCounts(prev => ({ ...prev, [playlistId]: json.totalAll }));
    } catch {
      /* ignore */
    }
  };

  const handleInclude = async userId => {
    setExcludeLoading(userId);
    try {
      await fetch(
        `${API_URL}/playlists/${selectedPlaylistId}/gestion-include`,
        {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({ user_id: userId }),
        }
      );
      fetchContacts(
        selectedPlaylistId,
        contactsPage,
        contactsSearch,
        contactsFilter
      );
      refreshPlaylistCount(selectedPlaylistId);
    } finally {
      setExcludeLoading(null);
    }
  };

  const handleExclude = async userId => {
    setExcludeLoading(userId);
    try {
      await fetch(
        `${API_URL}/playlists/${selectedPlaylistId}/gestion-exclude`,
        {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({ user_id: userId }),
        }
      );
      fetchContacts(
        selectedPlaylistId,
        contactsPage,
        contactsSearch,
        contactsFilter
      );
      refreshPlaylistCount(selectedPlaylistId);
    } finally {
      setExcludeLoading(null);
    }
  };

  let searchTimeout = null;
  const handleContactsSearch = val => {
    setContactsSearch(val);
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      setContactsPage(1);
      fetchContacts(selectedPlaylistId, 1, val, contactsFilter);
    }, 400);
  };

  // ─── Filter summary ────────────────────────────────────

  // Filtered + sorted playlists
  const filteredPlaylists = playlists
    .filter(p => {
      if (playlistSearch) {
        const s = playlistSearch.toLowerCase();
        if (!(p.name || '').toLowerCase().includes(s)) return false;
      }
      if (playlistStatusFilter) {
        const bcs = playlistBroadcasts[p.id] || [];
        if (!bcs.some(b => broadcastMatchesStatus(b, playlistStatusFilter)))
          return false;
      }
      return true;
    })
    .sort((a, b) =>
      playlistSortAsc
        ? new Date(a.updated_at) - new Date(b.updated_at)
        : new Date(b.updated_at) - new Date(a.updated_at)
    );

  const filteredMessages = messages.filter(m => {
    if (!messageSearch) return true;
    const s = messageSearch.toLowerCase();
    return (
      (m.name || '').toLowerCase().includes(s) ||
      (m.subject || '').toLowerCase().includes(s)
    );
  });

  const selectedPlaylist = playlists.find(p => p.id === selectedPlaylistId);

  return (
    <div
      style={{
        display: 'flex',
        height: 'calc(100vh - 200px)',
        gap: 20,
      }}
    >
      {/* ─── LEFT panel (1/3) ─── */}
      <div
        style={{
          width: 500,
          minWidth: 500,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {(() => {
          const allB = Object.values(playlistBroadcasts || {}).flat();
          const running = allB.filter(
            b => b.status === 'active' || b.status === 'paused'
          ).length;
          const scheduled = allB.filter(
            b => b.status === 'new' && b.scheduled_start_at
          ).length;
          const isActive = rightPanel === null;
          const summary =
            running || scheduled
              ? ` (${running} en cours · ${scheduled} programmés)`
              : '';
          return (
            <Button
              type={isActive ? 'primary' : 'default'}
              icon={<SendOutlined />}
              onClick={() => {
                setRightPanel(null);
                setSelectedPlaylistId(null);
              }}
              block
              style={{
                marginBottom: 8,
                ...(isActive
                  ? {
                      background: '#0f172a',
                      borderColor: '#0f172a',
                      color: '#fff',
                    }
                  : {}),
              }}
            >
              Suivi campagnes{summary}
            </Button>
          );
        })()}
        <Tabs
          size="small"
          items={[
            {
              key: 'playlists',
              label: `Fichiers (${playlists.length})`,
              children: (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    height: 'calc(100vh - 260px)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <Input
                      placeholder="Rechercher..."
                      prefix={<SearchOutlined />}
                      value={playlistSearch}
                      onChange={e => setPlaylistSearch(e.target.value)}
                      allowClear
                      size="small"
                      style={{ flex: 1 }}
                    />
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      size="small"
                      onClick={() => {
                        setEditPlaylist(null);
                        setDrawerOpen(true);
                      }}
                      style={{ background: '#f97316', borderColor: '#f97316' }}
                    >
                      Nouveau
                    </Button>
                  </div>
                  <div
                    style={{ display: 'flex', gap: 4, alignItems: 'center' }}
                  >
                    <Button
                      size="small"
                      onClick={() => setPlaylistSortAsc(!playlistSortAsc)}
                      style={{ fontSize: 12 }}
                    >
                      {playlistSortAsc ? '↑ Anciennes' : '↓ Récentes'}
                    </Button>
                    {[
                      { key: 'preparing', label: 'En préparation' },
                      { key: 'scheduled', label: 'Programmés' },
                      { key: 'running', label: 'En cours' },
                      { key: 'completed', label: 'Terminés' },
                    ].map(opt => {
                      const isActive = playlistStatusFilter === opt.key;
                      const palette = STATUS_PALETTE[opt.key];
                      return (
                        <Button
                          key={opt.key}
                          size="small"
                          onClick={() =>
                            setPlaylistStatusFilter(isActive ? null : opt.key)
                          }
                          style={{
                            fontSize: 12,
                            ...(isActive
                              ? {
                                  background: palette.bg,
                                  color: palette.fg,
                                  borderColor: palette.border,
                                  fontWeight: 600,
                                }
                              : {}),
                          }}
                        >
                          {opt.label}
                        </Button>
                      );
                    })}
                    {playlistStatusFilter && (
                      <Button
                        size="small"
                        type="link"
                        onClick={() => setPlaylistStatusFilter(null)}
                        style={{ fontSize: 11, padding: 0 }}
                      >
                        Reset
                      </Button>
                    )}
                  </div>

                  <div
                    style={{
                      flex: 1,
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    {loading ? (
                      <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin />
                      </div>
                    ) : filteredPlaylists.length === 0 ? (
                      <Empty
                        description="Aucune playlist"
                        style={{ marginTop: 40 }}
                      />
                    ) : (
                      filteredPlaylists.map(pl => {
                        const excluded =
                          typeof pl.excluded_user_ids === 'string'
                            ? JSON.parse(pl.excluded_user_ids)
                            : pl.excluded_user_ids || [];
                        const isSelected = selectedPlaylistId === pl.id;
                        const f =
                          typeof pl.gestion_filters === 'string'
                            ? JSON.parse(pl.gestion_filters)
                            : pl.gestion_filters || {};
                        const bcs = playlistBroadcasts[pl.id] || [];
                        const counts = {
                          preparing: bcs.filter(b =>
                            broadcastMatchesStatus(b, 'preparing')
                          ).length,
                          scheduled: bcs.filter(b =>
                            broadcastMatchesStatus(b, 'scheduled')
                          ).length,
                          running: bcs.filter(b =>
                            broadcastMatchesStatus(b, 'running')
                          ).length,
                          completed: bcs.filter(b =>
                            broadcastMatchesStatus(b, 'completed')
                          ).length,
                        };
                        // Statut le plus "chaud" pour le point coloré
                        const hottest = counts.running
                          ? 'running'
                          : counts.scheduled
                            ? 'scheduled'
                            : counts.preparing
                              ? 'preparing'
                              : counts.completed
                                ? 'completed'
                                : null;
                        const dotColor = hottest
                          ? STATUS_PALETTE[hottest].fg
                          : '#cbd5e1';

                        // Résumé filtres (1 ligne)
                        const tagIds = f.tag_ids || [];
                        const deptIds = f.departement_ids || [];
                        const regionIds = f.region_ids || [];
                        const filterParts = [];
                        if (regionIds.length)
                          filterParts.push(
                            `${regionIds.length} région${regionIds.length > 1 ? 's' : ''}`
                          );
                        if (deptIds.length)
                          filterParts.push(`${deptIds.length} dép.`);
                        if (tagIds.length) {
                          const tagNames = tagIds
                            .map(id => tags.find(t => t.id === id)?.nom)
                            .filter(Boolean)
                            .slice(0, 2)
                            .join(', ');
                          filterParts.push(
                            tagIds.length > 2 ? `${tagNames}…` : tagNames
                          );
                        }
                        if (f.client_filter && f.client_filter !== 'all')
                          filterParts.push(CLIENT_LABELS[f.client_filter]);
                        if (excluded.length)
                          filterParts.push(`${excluded.length} excl.`);

                        // Calcul "+N new" si broadcast existant
                        let newDelta = 0;
                        if (bcs.length && playlistCounts[pl.id] !== undefined) {
                          const lastBroadcast = bcs.reduce((a, b) =>
                            new Date(b.created_at) > new Date(a.created_at)
                              ? b
                              : a
                          );
                          newDelta =
                            playlistCounts[pl.id] -
                            (lastBroadcast.total_to_send || 0);
                        }

                        return (
                          <div
                            key={pl.id}
                            onClick={() => openContacts(pl.id)}
                            style={{
                              borderLeft: isSelected
                                ? '3px solid #f97316'
                                : '3px solid transparent',
                              borderTop: '1px solid #f1f5f9',
                              borderRight: '1px solid #f1f5f9',
                              borderBottom: '1px solid #f1f5f9',
                              borderRadius: 6,
                              padding: '8px 10px',
                              background: isSelected ? '#fff7ed' : '#fff',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                            }}
                            onMouseEnter={e => {
                              if (!isSelected)
                                e.currentTarget.style.background = '#f9fafb';
                            }}
                            onMouseLeave={e => {
                              if (!isSelected)
                                e.currentTarget.style.background = '#fff';
                            }}
                          >
                            {/* Dot statut */}
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: dotColor,
                                flexShrink: 0,
                              }}
                            />

                            {/* Nom + filtres résumés */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: 13,
                                  color: '#1e293b',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {pl.name}
                              </div>
                              {filterParts.length > 0 && (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: '#94a3b8',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {filterParts.join(' · ')}
                                </div>
                              )}
                            </div>

                            {/* Compteur contacts */}
                            <div
                              style={{
                                fontSize: 12,
                                color: '#475569',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {playlistCounts[pl.id] !== undefined
                                ? playlistCounts[pl.id].toLocaleString('fr-FR')
                                : '…'}
                              {newDelta > 0 && (
                                <span
                                  style={{
                                    color: '#16a34a',
                                    marginLeft: 4,
                                    fontWeight: 500,
                                  }}
                                >
                                  +{newDelta.toLocaleString('fr-FR')}
                                </span>
                              )}
                            </div>

                            {/* Kebab menu */}
                            <Dropdown
                              trigger={['click']}
                              menu={{
                                items: [
                                  {
                                    key: 'campaigns',
                                    icon: <SendOutlined />,
                                    label: 'Voir les campagnes',
                                    onClick: ({ domEvent }) => {
                                      domEvent.stopPropagation();
                                      setSelectedPlaylistId(pl.id);
                                      setRightPanel('broadcasts');
                                    },
                                  },
                                  {
                                    key: 'edit',
                                    icon: <EditOutlined />,
                                    label: 'Modifier',
                                    onClick: ({ domEvent }) => {
                                      domEvent.stopPropagation();
                                      setEditPlaylist(pl);
                                      setDrawerOpen(true);
                                    },
                                  },
                                  { type: 'divider' },
                                  {
                                    key: 'archive',
                                    icon: <DeleteOutlined />,
                                    label: 'Archiver',
                                    danger: true,
                                    onClick: ({ domEvent }) => {
                                      domEvent.stopPropagation();
                                      Modal.confirm({
                                        title: 'Archiver cette playlist ?',
                                        content:
                                          'Elle ne sera plus visible mais pas supprimée.',
                                        okText: 'Archiver',
                                        cancelText: 'Annuler',
                                        okButtonProps: { danger: true },
                                        onOk: () => handleArchive(pl.id),
                                      });
                                    },
                                  },
                                ],
                              }}
                            >
                              <Button
                                type="text"
                                icon={<MoreOutlined style={{ fontSize: 22 }} />}
                                onClick={e => e.stopPropagation()}
                                style={{
                                  width: 36,
                                  height: 36,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              />
                            </Dropdown>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ),
            },
            {
              key: 'messages',
              label: `Messages (${messages.length})`,
              children: (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    height: 'calc(100vh - 260px)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <Input
                      placeholder="Rechercher..."
                      prefix={<SearchOutlined />}
                      value={messageSearch}
                      onChange={e => setMessageSearch(e.target.value)}
                      allowClear
                      size="small"
                      style={{ flex: 1 }}
                    />
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      size="small"
                      onClick={() => {
                        setEditMessage(null);
                        setMessageDrawerOpen(true);
                      }}
                      style={{ background: '#f97316', borderColor: '#f97316' }}
                    >
                      Nouveau
                    </Button>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    }}
                  >
                    {filteredMessages.length === 0 ? (
                      <Empty
                        description="Aucun message"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        style={{ margin: '16px 0' }}
                      />
                    ) : (
                      filteredMessages.map(m => (
                        <div
                          key={m.id}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: 8,
                            padding: '8px 12px',
                            background: '#fff',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              marginBottom: 4,
                            }}
                          >
                            <MailOutlined
                              style={{ color: '#f97316', fontSize: 13 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: 14,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {m.name || m.subject}
                              </div>
                              {m.name && (
                                <div
                                  style={{
                                    fontSize: 13,
                                    color: '#94a3b8',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {m.subject}
                                </div>
                              )}
                            </div>
                          </div>
                          {m.from_name && (
                            <div style={{ fontSize: 13, color: '#64748b' }}>
                              {m.from_name}
                              {m.from_email && ` <${m.from_email}>`}
                            </div>
                          )}
                          <div
                            style={{
                              display: 'flex',
                              gap: 4,
                              marginTop: 6,
                            }}
                          >
                            <Button
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => {
                                setEditMessage(m);
                                setMessageDrawerOpen(true);
                              }}
                            >
                              Modifier
                            </Button>
                            <Button
                              size="small"
                              icon={<ExperimentOutlined />}
                              onClick={() => openTestMessage(m.id)}
                            >
                              Tester
                            </Button>
                            <Button
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => handleDuplicateMessage(m.id)}
                            >
                              Dupliquer
                            </Button>
                            <Popconfirm
                              title="Archiver ce message ?"
                              onConfirm={() => handleDeleteMessage(m.id)}
                              okText="Archiver"
                              cancelText="Annuler"
                            >
                              <Button
                                size="small"
                                icon={<DeleteOutlined />}
                                danger
                              />
                            </Popconfirm>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>

      {/* ─── RIGHT: Contacts panel (2/3) ─── */}
      <div
        style={{
          flex: 1,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {rightPanel === 'contacts' && selectedPlaylist ? (
          <>
            {/* Header */}
            <div
              style={{
                padding: '14px 20px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>
                  {selectedPlaylist.name}
                </div>
                <Text style={{ fontSize: 13, color: '#64748b' }}>
                  {contactsTotal} contacts • {excludedCount} exclus
                </Text>
              </div>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => {
                  setRightPanel(null);
                  setSelectedPlaylistId(null);
                }}
                size="small"
              >
                Fermer
              </Button>
            </div>

            {/* Search + Filter */}
            <div style={{ padding: '10px 20px 0' }}>
              <Input
                placeholder="Rechercher nom, email, SIRET..."
                prefix={<SearchOutlined />}
                value={contactsSearch}
                onChange={e => handleContactsSearch(e.target.value)}
                allowClear
                style={{ marginBottom: 8 }}
              />
              <Radio.Group
                value={contactsFilter}
                onChange={e => {
                  const v = e.target.value;
                  setContactsFilter(v);
                  setContactsPage(1);
                  fetchContacts(selectedPlaylistId, 1, contactsSearch, v);
                }}
                size="small"
              >
                <Radio.Button value="active">Actifs</Radio.Button>
                <Radio.Button value="excluded">
                  Exclus ({excludedCount})
                </Radio.Button>
                <Radio.Button value="lr">Liste rouge</Radio.Button>
                <Radio.Button value="incomplete">Incomplets</Radio.Button>
              </Radio.Group>
            </div>

            {/* Contacts grid */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '12px 20px',
              }}
            >
              {contactsLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <Spin />
                </div>
              ) : contacts.length === 0 ? (
                <Empty description="Aucun contact" />
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 8,
                  }}
                >
                  {contacts.map(c => (
                    <div
                      key={c.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 10px',
                        border: '1px solid #f0f0f0',
                        borderRadius: 8,
                        background: '#fafafa',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 13,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {(!c.civilite ||
                            !c.nom ||
                            !c.prenom ||
                            !c.departement) && (
                            <span
                              title={[
                                !c.civilite && 'Civilité manquante',
                                !c.nom && 'Nom manquant',
                                !c.prenom && 'Prénom manquant',
                                !c.departement && 'Département manquant',
                              ]
                                .filter(Boolean)
                                .join(', ')}
                              style={{ color: '#f59e0b', marginRight: 4 }}
                            >
                              ⚠
                            </span>
                          )}
                          {c.civilite && (
                            <span style={{ color: '#888', fontWeight: 400 }}>
                              {c.civilite}{' '}
                            </span>
                          )}
                          {`${c.prenom || ''} ${c.nom || ''}`.trim() || '–'}
                        </div>
                        {c.nom_commercial && (
                          <div
                            style={{
                              fontSize: 13,
                              color: '#1d4ed8',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {c.nom_commercial}
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 13,
                            color: '#555',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.email || '–'}
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: '#888',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.telephone && <span>{c.telephone}</span>}
                          {c.telephone && c.departement && ' • '}
                          {c.departement && <span>{c.departement}</span>}
                        </div>
                        {((c.tags && c.tags.length > 0) ||
                          c.is_lr ||
                          c.is_excluded) && (
                          <div style={{ marginTop: 3 }}>
                            {c.is_excluded && (
                              <Tag
                                style={{
                                  fontSize: 13,
                                  padding: '0 4px',
                                  lineHeight: '18px',
                                  background: '#faad14',
                                  color: '#fff',
                                  border: 'none',
                                }}
                              >
                                Exclu
                              </Tag>
                            )}
                            {c.is_lr && (
                              <Tag
                                style={{
                                  fontSize: 13,
                                  padding: '0 4px',
                                  lineHeight: '18px',
                                  background: '#ff4d4f',
                                  color: '#fff',
                                  border: 'none',
                                }}
                              >
                                LR
                              </Tag>
                            )}
                            {(c.tags || []).map(t => (
                              <Tag
                                key={t.id}
                                style={{
                                  fontSize: 13,
                                  padding: '0 4px',
                                  lineHeight: '18px',
                                  background: t.color_bkg || '#eee',
                                  color: t.color_text || '#333',
                                  border: 'none',
                                }}
                              >
                                {t.nom}
                              </Tag>
                            ))}
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                        }}
                      >
                        <Tooltip title="Voir dans Gestion">
                          <Button
                            size="small"
                            icon={<LinkOutlined />}
                            href={`${window.location.protocol}//${window.location.hostname === 'localhost' ? 'localhost:81' : 'gestion.devis-localise.com'}/gestion/${c.form_id || c.id}`}
                            target="_blank"
                          />
                        </Tooltip>
                        {c.is_excluded ? (
                          <Tooltip title="Réintégrer">
                            <Button
                              size="small"
                              icon={<UndoOutlined />}
                              loading={excludeLoading === c.id}
                              onClick={() => handleInclude(c.id)}
                              style={{
                                color: '#52c41a',
                                borderColor: '#b7eb8f',
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Exclure">
                            <Button
                              size="small"
                              icon={<StopOutlined />}
                              danger
                              loading={excludeLoading === c.id}
                              onClick={() => handleExclude(c.id)}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {contactsTotal > pageSize && (
              <div
                style={{
                  padding: '10px 20px',
                  borderTop: '1px solid #f0f0f0',
                  textAlign: 'center',
                }}
              >
                <Pagination
                  current={contactsPage}
                  total={contactsTotal}
                  pageSize={pageSize}
                  onChange={p => {
                    setContactsPage(p);
                    fetchContacts(
                      selectedPlaylistId,
                      p,
                      contactsSearch,
                      contactsFilter
                    );
                  }}
                  size="small"
                  showSizeChanger={false}
                />
              </div>
            )}
          </>
        ) : rightPanel === 'broadcasts' && selectedPlaylist ? (
          <>
            {/* Broadcasts header */}
            <div
              style={{
                padding: '14px 20px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>
                  Campagnes : {selectedPlaylist.name}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size="small"
                  onClick={() => {
                    setBroadcastPlaylist(selectedPlaylist);
                    setBroadcastDrawerOpen(true);
                  }}
                  style={{ background: '#f97316', borderColor: '#f97316' }}
                >
                  Nouveau
                </Button>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => {
                    setRightPanel(null);
                    setSelectedPlaylistId(null);
                  }}
                  size="small"
                >
                  Fermer
                </Button>
              </div>
            </div>

            {/* Broadcasts search */}
            <div style={{ padding: '10px 20px 0' }}>
              <Input
                placeholder="Rechercher une campagne..."
                prefix={<SearchOutlined />}
                value={broadcastSearch}
                onChange={e => {
                  setBroadcastSearch(e.target.value);
                  setBroadcastPage(1);
                }}
                allowClear
                size="small"
              />
            </div>

            {/* Broadcasts list */}
            {(() => {
              const allBroadcasts =
                playlistBroadcasts[selectedPlaylistId] || [];
              const filtered = broadcastSearch
                ? allBroadcasts.filter(b => {
                    const s = broadcastSearch.toLowerCase();
                    const msgName = b.message_id
                      ? messages.find(m => m.id === b.message_id)?.name ||
                        messages.find(m => m.id === b.message_id)?.subject ||
                        ''
                      : '';
                    return (
                      (b.name || '').toLowerCase().includes(s) ||
                      (b.smtp_provider_name || '').toLowerCase().includes(s) ||
                      msgName.toLowerCase().includes(s)
                    );
                  })
                : allBroadcasts;
              const sorted = [...filtered].sort(
                (a, b) =>
                  new Date(b.created_at || 0) - new Date(a.created_at || 0)
              );
              const totalPages = Math.ceil(sorted.length / broadcastPageSize);
              const paginated = sorted.slice(
                (broadcastPage - 1) * broadcastPageSize,
                broadcastPage * broadcastPageSize
              );

              return (
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '12px 20px',
                  }}
                >
                  {sorted.length === 0 ? (
                    <Empty description="Aucune campagne" />
                  ) : (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 6,
                        }}
                      >
                        {paginated.map(b => {
                          const messageName = b.message_id
                            ? messages.find(m => m.id === b.message_id)?.name ||
                              messages.find(m => m.id === b.message_id)
                                ?.subject ||
                              '— message —'
                            : '— pas de message —';
                          const dateInfo = formatBroadcastDate(b);
                          return (
                            <div
                              key={b.id}
                              onClick={() => setDetailBroadcast(b)}
                              style={{
                                display: 'flex',
                                alignItems: 'stretch',
                                gap: 12,
                                padding: '10px 12px',
                                border: '1px solid #e5e7eb',
                                borderRadius: 8,
                                cursor: 'pointer',
                                background: '#fff',
                              }}
                              onMouseEnter={e =>
                                (e.currentTarget.style.background = '#f9fafb')
                              }
                              onMouseLeave={e =>
                                (e.currentTarget.style.background = '#fff')
                              }
                            >
                              <div
                                style={{
                                  minWidth: 76,
                                  textAlign: 'center',
                                  paddingRight: 12,
                                  borderRight: '1px solid #f1f5f9',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: '#94a3b8',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.4,
                                  }}
                                >
                                  {dateInfo.label}
                                </div>
                                <div
                                  style={{
                                    fontSize: 22,
                                    fontWeight: 700,
                                    lineHeight: 1.1,
                                    color: '#0f172a',
                                  }}
                                >
                                  {dateInfo.day}
                                </div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                  {dateInfo.month}
                                </div>
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  gap: 2,
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    minWidth: 0,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontWeight: 600,
                                      fontSize: 13,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flex: 1,
                                    }}
                                  >
                                    {b.name || selectedPlaylist.name}
                                  </div>
                                  {renderBroadcastStatusTag(b)}
                                  <Dropdown
                                    trigger={['click']}
                                    menu={{
                                      items: [
                                        {
                                          key: 'duplicate',
                                          icon: <CopyOutlined />,
                                          label: 'Dupliquer',
                                          onClick: ({ domEvent }) => {
                                            domEvent.stopPropagation();
                                            setBroadcastPlaylist(
                                              selectedPlaylist
                                            );
                                            setDuplicateFrom(b);
                                            setBroadcastDrawerOpen(true);
                                          },
                                        },
                                        {
                                          key: 'archive',
                                          icon: <DeleteOutlined />,
                                          label: 'Archiver',
                                          danger: true,
                                          onClick: ({ domEvent }) => {
                                            domEvent.stopPropagation();
                                            Modal.confirm({
                                              title:
                                                'Archiver cette campagne ?',
                                              content:
                                                'Elle ne sera plus visible mais pas supprimée.',
                                              okText: 'Archiver',
                                              cancelText: 'Annuler',
                                              okButtonProps: { danger: true },
                                              onOk: async () => {
                                                try {
                                                  await fetch(
                                                    `${API_URL}/gestion-broadcasts/${b.id}/archive`,
                                                    {
                                                      method: 'POST',
                                                      headers: jsonHeaders,
                                                    }
                                                  );
                                                  message.success(
                                                    'Campagne archivée'
                                                  );
                                                  fetchAllBroadcasts();
                                                } catch {
                                                  message.error(
                                                    "Erreur lors de l'archivage"
                                                  );
                                                }
                                              },
                                            });
                                          },
                                        },
                                      ],
                                    }}
                                  >
                                    <Button
                                      size="small"
                                      type="text"
                                      icon={<MoreOutlined />}
                                      onClick={e => e.stopPropagation()}
                                    />
                                  </Dropdown>
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: '#94a3b8',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {messageName}
                                </div>
                                {(b.status === 'active' ||
                                  b.status === 'paused' ||
                                  b.status === 'completed') && (
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: '#475569',
                                    }}
                                  >
                                    {b.status !== 'completed' && (
                                      <>
                                        Jour {b.current_day}/
                                        {b.warmup_total_days || '?'} —{' '}
                                      </>
                                    )}
                                    {b.total_sent || 0}
                                    {b.total_to_send
                                      ? `/${b.total_to_send}`
                                      : ''}{' '}
                                    envoyés
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {totalPages > 1 && (
                        <div style={{ textAlign: 'center', marginTop: 12 }}>
                          <Pagination
                            current={broadcastPage}
                            total={sorted.length}
                            pageSize={broadcastPageSize}
                            onChange={p => setBroadcastPage(p)}
                            size="small"
                            showSizeChanger={false}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </>
        ) : (
          (() => {
            const isArchivedView = activityFilter === 'archived';
            const allB = isArchivedView
              ? archivedBroadcasts
              : Object.values(playlistBroadcasts || {}).flat();
            const playlistNameById = Object.fromEntries(
              playlists.map(p => [p.id, p.name])
            );
            const messageNameById = Object.fromEntries(
              messages.map(m => [m.id, m.name || m.subject])
            );

            const matchFilter = b => {
              if (activityFilter === 'all' || activityFilter === 'archived')
                return true;
              return broadcastMatchesStatus(b, activityFilter);
            };
            const matchSearch = b => {
              if (!activitySearch) return true;
              const s = activitySearch.toLowerCase();
              const pName = (
                playlistNameById[b.playlist_id] || ''
              ).toLowerCase();
              const mName = b.message_id
                ? (messageNameById[b.message_id] || '').toLowerCase()
                : '';
              return (
                (b.name || '').toLowerCase().includes(s) ||
                pName.includes(s) ||
                mName.includes(s)
              );
            };

            // Date de référence par broadcast (pour tri chrono)
            const refDate = b => {
              if (b.status === 'new')
                return b.scheduled_start_at || b.created_at;
              if (b.status === 'active' || b.status === 'paused')
                return b.activated_at || b.updated_at;
              if (b.status === 'completed')
                return b.updated_at || b.activated_at;
              return b.created_at;
            };

            const filtered = allB
              .filter(matchFilter)
              .filter(matchSearch)
              .sort((a, b) => {
                const da = new Date(refDate(a) || 0).getTime();
                const db = new Date(refDate(b) || 0).getTime();
                return activitySortDesc ? db - da : da - db;
              });

            // Compteurs : stables, indépendants de la vue active
            const activeFlat = Object.values(playlistBroadcasts || {}).flat();
            const counts = {
              all: activeFlat.length,
              preparing: activeFlat.filter(b =>
                broadcastMatchesStatus(b, 'preparing')
              ).length,
              scheduled: activeFlat.filter(b =>
                broadcastMatchesStatus(b, 'scheduled')
              ).length,
              running: activeFlat.filter(b =>
                broadcastMatchesStatus(b, 'running')
              ).length,
              completed: activeFlat.filter(b =>
                broadcastMatchesStatus(b, 'completed')
              ).length,
              archived: archivedBroadcasts.length,
            };

            const renderStatus = b => {
              if (b.status === 'new' && b.scheduled_start_at)
                return pastelTag(STATUS_PALETTE.scheduled, 'Programmé');
              if (b.status === 'new')
                return pastelTag(STATUS_PALETTE.preparing, 'En préparation');
              if (b.status === 'active')
                return pastelTag(STATUS_PALETTE.running, 'En cours');
              if (b.status === 'paused')
                return pastelTag(STATUS_PALETTE.paused, 'En pause');
              if (b.status === 'completed')
                return pastelTag(STATUS_PALETTE.completed, 'Terminé');
              return null;
            };

            const formatRefDate = b => {
              const d = refDate(b);
              if (!d) return { day: '—', month: '', label: '' };
              const date = new Date(d);
              const day = date.toLocaleDateString('fr-FR', {
                day: '2-digit',
              });
              const month = date.toLocaleDateString('fr-FR', {
                month: 'short',
              });
              const year = date.getFullYear();
              const labelByStatus = {
                new: b.scheduled_start_at ? 'Lancement' : 'Créé le',
                active: 'Démarré le',
                paused: 'Démarré le',
                completed: 'Terminé le',
              };
              return {
                day,
                month: `${month} ${year}`,
                label: labelByStatus[b.status] || '',
              };
            };

            return (
              <>
                {/* Header */}
                <div
                  style={{
                    padding: '14px 20px',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>
                      Suivi campagnes
                    </div>
                    <Text style={{ fontSize: 13, color: '#64748b' }}>
                      {filtered.length} sur {allB.length}
                    </Text>
                  </div>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="small"
                    onClick={() => {
                      setBroadcastPlaylist(null);
                      setBroadcastDrawerOpen(true);
                    }}
                    style={{ background: '#f97316', borderColor: '#f97316' }}
                  >
                    Nouvelle campagne
                  </Button>
                </div>

                {/* Search + Filters */}
                <div style={{ padding: '10px 20px 0' }}>
                  <Input
                    placeholder="Rechercher par campagne, playlist, message..."
                    prefix={<SearchOutlined />}
                    value={activitySearch}
                    onChange={e => setActivitySearch(e.target.value)}
                    allowClear
                    style={{ marginBottom: 8 }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <Radio.Group
                      value={activityFilter}
                      onChange={e => setActivityFilter(e.target.value)}
                      size="small"
                    >
                      <Radio.Button value="all">
                        Tous ({counts.all})
                      </Radio.Button>
                      <Radio.Button value="preparing">
                        En préparation ({counts.preparing})
                      </Radio.Button>
                      <Radio.Button value="scheduled">
                        Programmés ({counts.scheduled})
                      </Radio.Button>
                      <Radio.Button value="running">
                        En cours ({counts.running})
                      </Radio.Button>
                      <Radio.Button value="completed">
                        Terminés ({counts.completed})
                      </Radio.Button>
                      <Radio.Button value="archived">
                        Archivés ({counts.archived})
                      </Radio.Button>
                    </Radio.Group>
                    <Button
                      size="small"
                      onClick={() => setActivitySortDesc(s => !s)}
                    >
                      Date {activitySortDesc ? '↓' : '↑'}
                    </Button>
                  </div>
                </div>

                {/* List */}
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '12px 20px',
                  }}
                >
                  {filtered.length === 0 ? (
                    <Empty description="Aucune campagne" />
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                      }}
                    >
                      {filtered.map(b => {
                        const playlistName =
                          playlistNameById[b.playlist_id] || '— playlist —';
                        const messageName = b.message_id
                          ? messageNameById[b.message_id] || '— message —'
                          : '— pas de message —';
                        const dateInfo = formatRefDate(b);
                        return (
                          <div
                            key={b.id}
                            onClick={() => setDetailBroadcast(b)}
                            style={{
                              display: 'flex',
                              alignItems: 'stretch',
                              gap: 12,
                              padding: '10px 12px',
                              border: '1px solid #e5e7eb',
                              borderRadius: 8,
                              cursor: 'pointer',
                              background: '#fff',
                            }}
                            onMouseEnter={e =>
                              (e.currentTarget.style.background = '#f9fafb')
                            }
                            onMouseLeave={e =>
                              (e.currentTarget.style.background = '#fff')
                            }
                          >
                            <div
                              style={{
                                minWidth: 76,
                                textAlign: 'center',
                                paddingRight: 12,
                                borderRight: '1px solid #f1f5f9',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 10,
                                  color: '#94a3b8',
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.4,
                                }}
                              >
                                {dateInfo.label}
                              </div>
                              <div
                                style={{
                                  fontSize: 22,
                                  fontWeight: 700,
                                  lineHeight: 1.1,
                                  color: '#0f172a',
                                }}
                              >
                                {dateInfo.day}
                              </div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>
                                {dateInfo.month}
                              </div>
                            </div>
                            <div
                              style={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                gap: 2,
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  minWidth: 0,
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: 13,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                  }}
                                >
                                  {b.name || playlistName}
                                </div>
                                {renderStatus(b)}
                                <Dropdown
                                  trigger={['click']}
                                  menu={{
                                    items: isArchivedView
                                      ? [
                                          {
                                            key: 'unarchive',
                                            icon: <UndoOutlined />,
                                            label: 'Désarchiver',
                                            onClick: async ({ domEvent }) => {
                                              domEvent.stopPropagation();
                                              try {
                                                await fetch(
                                                  `${API_URL}/gestion-broadcasts/${b.id}/unarchive`,
                                                  {
                                                    method: 'POST',
                                                    headers: jsonHeaders,
                                                  }
                                                );
                                                message.success(
                                                  'Campagne désarchivée'
                                                );
                                                fetchAllBroadcasts();
                                              } catch {
                                                message.error(
                                                  'Erreur lors du désarchivage'
                                                );
                                              }
                                            },
                                          },
                                        ]
                                      : [
                                          {
                                            key: 'archive',
                                            icon: <DeleteOutlined />,
                                            label: 'Archiver',
                                            danger: true,
                                            onClick: ({ domEvent }) => {
                                              domEvent.stopPropagation();
                                              Modal.confirm({
                                                title:
                                                  'Archiver cette campagne ?',
                                                content:
                                                  'Elle ne sera plus visible mais pas supprimée.',
                                                okText: 'Archiver',
                                                cancelText: 'Annuler',
                                                okButtonProps: { danger: true },
                                                onOk: async () => {
                                                  try {
                                                    await fetch(
                                                      `${API_URL}/gestion-broadcasts/${b.id}/archive`,
                                                      {
                                                        method: 'POST',
                                                        headers: jsonHeaders,
                                                      }
                                                    );
                                                    message.success(
                                                      'Campagne archivée'
                                                    );
                                                    fetchAllBroadcasts();
                                                  } catch {
                                                    message.error(
                                                      "Erreur lors de l'archivage"
                                                    );
                                                  }
                                                },
                                              });
                                            },
                                          },
                                        ],
                                  }}
                                >
                                  <Button
                                    size="small"
                                    type="text"
                                    icon={<MoreOutlined />}
                                    onClick={e => e.stopPropagation()}
                                  />
                                </Dropdown>
                              </div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: '#94a3b8',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {playlistName} · {messageName}
                              </div>
                              {(b.status === 'active' ||
                                b.status === 'paused' ||
                                b.status === 'completed') &&
                                (() => {
                                  const sent = b.total_sent || 0;
                                  const planned = b.total_planned || 0;
                                  const target = b.total_to_send || 0;
                                  const filtered = Math.max(
                                    target - planned,
                                    0
                                  );
                                  const failed = Math.max(planned - sent, 0);
                                  const tooltipContent = (
                                    <div style={{ fontSize: 12 }}>
                                      <div>
                                        <strong>{sent}</strong> envoyés
                                      </div>
                                      {filtered > 0 && (
                                        <div>
                                          <strong>{filtered}</strong> filtrés
                                          (bounces / désabos / doublons)
                                        </div>
                                      )}
                                      {failed > 0 && (
                                        <div>
                                          <strong>{failed}</strong> en échec
                                          SMTP
                                        </div>
                                      )}
                                      <div
                                        style={{
                                          marginTop: 4,
                                          opacity: 0.7,
                                        }}
                                      >
                                        Total snapshot : {target}
                                      </div>
                                    </div>
                                  );
                                  return (
                                    <Tooltip title={tooltipContent}>
                                      <div
                                        style={{
                                          fontSize: 12,
                                          color: '#475569',
                                          cursor: 'help',
                                          display: 'inline-block',
                                        }}
                                      >
                                        {b.status !== 'completed' && (
                                          <>
                                            Jour {b.current_day}/
                                            {b.warmup_total_days || '?'} —{' '}
                                          </>
                                        )}
                                        {sent}
                                        {target ? `/${target}` : ''} envoyés
                                      </div>
                                    </Tooltip>
                                  );
                                })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            );
          })()
        )}
      </div>

      {/* Drawer création / édition */}
      <GestionPlaylistDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditPlaylist(null);
        }}
        onSaved={() => {
          fetchPlaylists();
          if (selectedPlaylistId) {
            fetchContacts(selectedPlaylistId, contactsPage, contactsSearch);
          }
        }}
        playlist={editPlaylist}
        tags={tags}
        activites={activites}
        regions={regions}
        departements={departements}
      />

      {/* Drawer broadcast */}
      <GestionBroadcastDrawer
        open={broadcastDrawerOpen}
        onClose={() => {
          setBroadcastDrawerOpen(false);
          setBroadcastPlaylist(null);
          setDuplicateFrom(null);
        }}
        onCreated={({ playlistId } = {}) => {
          fetchAllBroadcasts();
          if (playlistId) {
            setSelectedPlaylistId(playlistId);
            setRightPanel('broadcasts');
          }
        }}
        playlist={broadcastPlaylist}
        playlists={playlists}
        messages={messages}
        duplicateFrom={duplicateFrom}
        onMessagesChange={fetchMessages}
        userSignature={user?.email_signature}
        onSignatureUpdated={sig => {
          if (user) user.email_signature = sig;
        }}
      />

      {/* Drawer broadcast detail */}
      <GestionBroadcastDetailDrawer
        open={!!detailBroadcast}
        onClose={() => setDetailBroadcast(null)}
        broadcast={detailBroadcast}
        messageName={
          detailBroadcast?.message_id
            ? messages.find(m => m.id === detailBroadcast.message_id)?.name ||
              messages.find(m => m.id === detailBroadcast.message_id)?.subject
            : null
        }
        messageSubject={
          detailBroadcast?.message_id
            ? messages.find(m => m.id === detailBroadcast.message_id)?.subject
            : null
        }
        messageSubjectVariants={(() => {
          if (!detailBroadcast?.message_id) return [];
          const m = messages.find(msg => msg.id === detailBroadcast.message_id);
          if (!m?.subject_variants) return [];
          const v =
            typeof m.subject_variants === 'string'
              ? JSON.parse(m.subject_variants)
              : m.subject_variants;
          return Array.isArray(v) ? v.filter(s => s) : [];
        })()}
        messageHtml={
          detailBroadcast?.message_id
            ? messages.find(m => m.id === detailBroadcast.message_id)
                ?.html_content
            : null
        }
      />

      {/* Modal test message */}
      <Modal
        title="Envoyer un test"
        open={!!testMessageId}
        onCancel={() => setTestMessageId(null)}
        onOk={handleSendTest}
        confirmLoading={testSending}
        okText="Envoyer"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <Text
              strong
              style={{ fontSize: 13, display: 'block', marginBottom: 4 }}
            >
              Email destinataire
            </Text>
            <Input
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="email@test.com"
              onPressEnter={handleSendTest}
            />
          </div>
          <div>
            <Text
              strong
              style={{ fontSize: 13, display: 'block', marginBottom: 4 }}
            >
              Provider SMTP
            </Text>
            <Select
              placeholder="Choisir un provider"
              value={testProviderId}
              onChange={setTestProviderId}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              style={{ width: '100%' }}
              options={(() => {
                const groups = {};
                testProviders.forEach(p => {
                  const s = p.server || 'Autre';
                  if (!groups[s]) groups[s] = [];
                  groups[s].push(p);
                });
                return Object.entries(groups).map(([server, provs]) => ({
                  label: server,
                  options: provs.map(p => ({
                    value: p.id,
                    label: `${p.name} (${p.zone || p.outbound_ip || ''})`,
                  })),
                }));
              })()}
            />
          </div>
        </div>
      </Modal>

      {/* Drawer message editor */}
      <MessageEditorDrawer
        open={messageDrawerOpen}
        onClose={() => {
          setMessageDrawerOpen(false);
          setEditMessage(null);
        }}
        onSaved={fetchMessages}
        msg={editMessage}
        userId={user?.id}
        userSignature={user?.email_signature}
        onSignatureUpdated={sig => {
          // Met à jour le user en mémoire
          if (user) user.email_signature = sig;
        }}
      />
    </div>
  );
}
